import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssistantDigest,
  AssetDetail,
  AssetListItem,
  AuditRecord,
  DashboardOverview,
  EmployeeDetail,
  EmployeeListItem,
  ImportPlan,
  LaptopIntelligence,
  LicenseDetail,
  LicenseFacets,
  LicenseInsights,
  LicenseListItem,
  Meta,
  MovementDetail,
  MovementSummary,
  MyDevices,
  Page,
  SearchResults,
  SeatAssignmentRow,
  WarrantyItem,
  WarrantySummary,
} from '@skhub/contracts';
import { api } from './api';

type Params = Record<string, string | number | undefined>;

export interface AuditFacets {
  actions: { action: string; count: number }[];
  roles: string[];
  totals: { all: number; last24h: number; last7d: number; notSucceeded: number; actors: number };
  categories: { key: string; count: number }[];
}

export const useMeta = () => useQuery({ queryKey: ['meta'], queryFn: () => api.get<Meta>('/insights/meta'), staleTime: 5 * 60_000 });
export const useDashboard = () => useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<DashboardOverview>('/insights/dashboard') });
/** IT Assistant digest: loaded with the app (its counts drive the launcher badge) and refreshed after every data change. */
export const useAssistantDigest = (enabled: boolean) =>
  useQuery({ queryKey: ['assistant', 'digest'], queryFn: () => api.get<AssistantDigest>('/assistant/digest'), enabled, staleTime: 60_000 });
export const useLaptops = () => useQuery({ queryKey: ['laptops'], queryFn: () => api.get<LaptopIntelligence>('/insights/laptops') });

export const useAssets = (params: Params) =>
  useQuery({ queryKey: ['assets', params], queryFn: () => api.get<Page<AssetListItem>>('/assets', params), placeholderData: keepPreviousData });
export const useAsset = (id: string | undefined) =>
  useQuery({ queryKey: ['asset', id], queryFn: () => api.get<AssetDetail>(`/assets/${id}`), enabled: !!id });

export const useEmployees = (params: Params) =>
  useQuery({ queryKey: ['employees', params], queryFn: () => api.get<Page<EmployeeListItem>>('/employees', params), placeholderData: keepPreviousData });
export const useEmployee = (id: string | undefined) =>
  useQuery({ queryKey: ['employee', id], queryFn: () => api.get<EmployeeDetail>(`/employees/${id}`), enabled: !!id });

export const useMyDevices = () => useQuery({ queryKey: ['me', 'devices'], queryFn: () => api.get<MyDevices>('/me/devices') });

export const useAudit = (params: Params) =>
  useQuery({ queryKey: ['audit', params], queryFn: () => api.get<Page<AuditRecord>>('/audit', params), placeholderData: keepPreviousData });
export const useAuditFacets = () =>
  useQuery({ queryKey: ['audit', 'facets'], queryFn: () => api.get<AuditFacets>('/audit/facets'), staleTime: 60_000 });

export const useSearch = (q: string, enabled = true) =>
  useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => api.get<SearchResults>('/search', { q }, { signal }),
    enabled: enabled && q.trim().length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

export interface ImportSummary {
  batchId: string;
  fileName: string;
  status: string;
  totalRows: number;
  newRows: number;
  updateRows: number;
  unchangedRows: number;
  invalidRows: number;
  conflictRows: number;
  createdAt: string;
  createdByName: string;
  committedAt: string | null;
  committedByName: string | null;
}
export const useImports = () => useQuery({ queryKey: ['imports'], queryFn: () => api.get<ImportSummary[]>('/imports') });
export const useImport = (id: string | undefined) =>
  useQuery({ queryKey: ['import', id], queryFn: () => api.get<ImportPlan>(`/imports/${id}`), enabled: !!id });

/** Inventory changed: refresh everything derived from it. */
export function useInvalidateInventory() {
  const qc = useQueryClient();
  return () =>
    Promise.all(
      ['assets', 'asset', 'dashboard', 'laptops', 'employees', 'employee', 'audit', 'meta', 'search', 'me', 'warranty', 'movements', 'assistant'].map((k) =>
        qc.invalidateQueries({ queryKey: [k] }),
      ),
    );
}

/** A mutation on one asset. The server returns the fresh detail, which is written straight into the cache. */
export function useAssetAction<T>(id: string, action: '' | 'assign' | 'return' | 'status' | 'retire' | 'reinstate') {
  const qc = useQueryClient();
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: T) => (action === '' ? api.put<AssetDetail>(`/assets/${id}`, body) : api.post<AssetDetail>(`/assets/${id}/${action}`, body)),
    onSuccess: (detail) => {
      qc.setQueryData(['asset', id], detail);
      void invalidate();
    },
  });
}

// ── Software & licences (own cache keys: hardware invalidation never touches them) ──

export const useLicenseInsights = (enabled = true) =>
  useQuery({ queryKey: ['licenses', 'insights'], queryFn: () => api.get<LicenseInsights>('/licenses/insights'), enabled });
export const useLicenseFacets = () =>
  useQuery({ queryKey: ['licenses', 'facets'], queryFn: () => api.get<LicenseFacets>('/licenses/facets'), staleTime: 5 * 60_000 });
export const useLicenses = (params: Params) =>
  useQuery({ queryKey: ['licenses', 'list', params], queryFn: () => api.get<Page<LicenseListItem>>('/licenses', params), placeholderData: keepPreviousData });
export const useLicense = (id: string | undefined) =>
  useQuery({ queryKey: ['licenses', 'detail', id], queryFn: () => api.get<LicenseDetail>(`/licenses/${id}`), enabled: !!id });
export const useSeats = (params: Params) =>
  useQuery({ queryKey: ['licenses', 'seats', params], queryFn: () => api.get<Page<SeatAssignmentRow>>('/licenses/seats', params), placeholderData: keepPreviousData });

/** Assign (no seatId) or release a named seat. The server returns the fresh licence detail. */
export function useSeatAction<T>(licenseId: string, seatId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => api.post<LicenseDetail>(seatId ? `/licenses/${licenseId}/seats/${seatId}/release` : `/licenses/${licenseId}/seats`, body),
    onSuccess: (detail) => {
      qc.setQueryData(['licenses', 'detail', licenseId], detail);
      void qc.invalidateQueries({ queryKey: ['licenses'] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
      void qc.invalidateQueries({ queryKey: ['assistant'] });
    },
  });
}

// ── Warranty and monthly movements (hardware) ──

export const useWarranty = (params: Params) =>
  useQuery({ queryKey: ['warranty', 'list', params], queryFn: () => api.get<Page<WarrantyItem>>('/warranty', params), placeholderData: keepPreviousData });
export const useWarrantySummary = (params: Params = {}, enabled = true) =>
  useQuery({ queryKey: ['warranty', 'summary', params], queryFn: () => api.get<WarrantySummary>('/warranty/summary', params), enabled });

/** Set an asset's purchase / warranty dates. Returns the fresh asset detail. */
export function useWarrantyUpdate(id: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateInventory();
  return useMutation({
    mutationFn: (body: { rowVersion: string; purchaseDate: string | null; warrantyExpiry: string | null }) => api.put<AssetDetail>(`/assets/${id}/warranty`, body),
    onSuccess: (detail) => {
      qc.setQueryData(['asset', id], detail);
      void invalidate();
    },
  });
}

export const useMovements = (params: Params) =>
  useQuery({ queryKey: ['movements', 'summary', params], queryFn: () => api.get<MovementSummary>('/reports/movements', params), placeholderData: keepPreviousData });
export const useMovementDetail = (params: Params, enabled = true) =>
  useQuery({ queryKey: ['movements', 'detail', params], queryFn: () => api.get<MovementDetail>('/reports/movements/detail', params), enabled, placeholderData: keepPreviousData });
